import React from 'react';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';

import type { DndDragHandleProps } from '~/components/core/DndOrderedList/DndOrderedList.types';
import cn from '~/Utils/cn';

type ReorderDragHandleProps = {
  dragHandleProps: DndDragHandleProps;
  className?: string;
  disabled?: boolean;
  // Grip icon size. Defaults to 16px (matches a neighbouring ThreeDotsMenu); callers with larger
  // adjacent controls (e.g. 24px) can bump it so the grip lines up.
  iconSize?: number | string;
};

// Drag handle for DnD-reorderable rows. Stops click propagation so grabbing the handle
// inside a collapsible card header never toggles the collapse (the header toggles on
// click). We must NOT stop mousedown propagation: pragmatic-drag-and-drop starts a mouse
// drag from the native mousedown, so swallowing it here would break dragging from the grip.
const ReorderDragHandle = ({ dragHandleProps, className, disabled = false, iconSize = 16 }: ReorderDragHandleProps) => (
  <button
    {...dragHandleProps}
    // Explicit type="button" (dragHandleProps already sets it) so this never depends on the
    // caller and can't default to type="submit" inside a form.
    type="button"
    disabled={disabled}
    onClick={(e) => e.stopPropagation()}
    // The focus ring lives on the handle itself, not on the row wrapper: a `focus-within`
    // ring on the wrapper also lit up when a nested field (e.g. an option input) took focus.
    className={cn(
      'flex cursor-grab items-center rounded border-none bg-transparent p-0 text-slate-500 outline-none focus-visible:ring-2 focus-visible:ring-teal-500 disabled:cursor-not-allowed disabled:opacity-40',
      className
    )}
  >
    {/* Size via iconSize (default 16px to match a neighbouring ThreeDotsMenu). MUI's default
        DragIndicator is 24px; callers set iconSize to line the grip up with their own controls. */}
    <DragIndicatorIcon style={{ fontSize: iconSize }} />
  </button>
);

export default ReorderDragHandle;
